import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { notificationsService } from '../../services/api';
import NotificationPreferences from './NotificationPreferences';

const PROMPT_DELAY_MS = 4000;

/**
 * Shown once, shortly after a signed-in user lands in the app, so they can pick
 * channels and grant push permission. Dismissal is stored server-side so we
 * don't nag again; settings remain available at /notifications.
 */
const NotificationPrompt: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setVisible(false);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    notificationsService.getPreferences().then((res) => {
      if (!cancelled && !res.onboarded) {
        timer = setTimeout(() => setVisible(true), PROMPT_DELAY_MS);
      }
    }).catch(() => undefined);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isAuthenticated, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  const dismiss = async () => {
    setVisible(false);
    await notificationsService.dismissOnboarding().catch(() => undefined);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-xl bg-white rounded-lg shadow-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900">Stay in the loop</h2>
        <p className="mt-1 mb-4 text-sm text-gray-600">
          Choose how you'd like to hear about trip requests, approvals and more. Your browser will ask for
          permission if you enable push notifications. You can change this any time.
        </p>
        <NotificationPreferences onSaved={() => setVisible(false)} onCancel={dismiss} saveLabel="Save & continue" />
      </div>
    </div>
  );
};

export default NotificationPrompt;
